<template>
  <v-layout
    app
    column
    class="align-center"
    >
      <div
        v-if="show && $route.meta.sw !== false"
        id="update-notice"
        class="update-pill mb-3"
        :class="{ 'update-pill--waiting': updateBlocked }"
        role="status"
        >
          <v-icon small class="update-pill__icon">
            {{ updateBlocked ? icons.mdiTimerSand : icons.mdiCellphoneArrowDown }}
          </v-icon>
          <div class="update-pill__text">
            <mrx-translation class="update-pill__title" :token="message"></mrx-translation>
            <template v-if="updateBlocked">
              <span class="update-pill__dot" aria-hidden="true"></span>
              <mrx-translation
                id="update-app-hint"
                class="update-pill__hint"
                token="app.serviceworker.messages.update_after_assessment"
                ></mrx-translation>
            </template>
          </div>
          <v-btn
            v-if="!updateBlocked"
            id="update-app"
            class="update-pill__action"
            rounded
            depressed
            small
            @click="update"
            >
              <mrx-translation token="app.serviceworker.button.update"></mrx-translation>
          </v-btn>
      </div>
      <v-row no-gutters class="mb-2" v-if="!readyForPwaInstall && $route.meta.sw !== false" style="width: 100%">
        <v-banner
          :icon="icons.mdiAlert"
          icon-color="red"
          class="warning-banner"
          single-line
          outlined
        >
          <mrx-translation :token="`app.device.list.offline_not_ready.title`"></mrx-translation>
        </v-banner>
      </v-row>
    </v-layout>
</template>

<script>
    import { register } from 'register-service-worker'
    import icons from '@/mixins/icons'
    import mrx from '@/mixins/mrx'
    import { mapActions as mapPiniaActions, mapState } from 'pinia'
    import { useAuthStore } from '@/stores/auth';
    import { useCollectStore } from '@/stores/collect';
    import { useOfflineStore } from '@/stores/healthcheck';
    import { useTestModeStore } from '@/stores/testMode';
    import { usePartnerConfigStore } from '@/stores/partnerConfig';
    import { canApplyUpdate } from '@/utils/pwaUpdatePolicy';
    import { activateWaitingWorker, getWaitingWorker } from '@/utils/serviceWorkerUpdate';
    import './styles/service-worker.css'

    let channel
    let activeRegistration = null

    export default {
        name: 'ServiceWorker',
        mixins: [icons, mrx],
        data() {
            return {
                message: null,
                show: false,
                assetsCached: false
            }
        },

        watch: {
            offlineSetting() {
                this.updateOfflineReadiness()
            },
            // whenever question changes, this function will run
            blockKeen: (val) => {
                try {
                    if (val) {
                        channel.postMessage({ type: 'BLOCK_KEEN' })
                    }
                    else {
                        channel.postMessage({ type: 'UNBLOCK_KEEN' })
                    }
                }
                catch (e) {
                    console.log(e)
                }
            },
            simmulateOffline: (val) => {
                try {
                    if (val) {
                        channel.postMessage({ type: 'OFFLINE_MODE' })
                    }
                    else {
                        channel.postMessage({ type: 'ONLINE_MODE' })
                    }
                }
                catch (e) {
                    console.log(e)
                }
            }
        },

        created() {
            /*
            let link = document.createElement('link')
            link.rel = 'manifest'
            link.href = 'manifest.json'
            document.head.append(link)
            */
            try {
                channel = new BroadcastChannel('sw-messages')
                channel.addEventListener('message', event => {
                    if (event.data.type === 'RECORD_UPLOADED') {
                        let setLastUploadDate = this.setLastUploadDate
                        let recordsInQueue = this.recordsInQueue
                        setTimeout(function() {
                            recordsInQueue()
                            setLastUploadDate()
                        }, 10000)
                    }
                })
            }
            catch (e) {
                console.log('Service worker functionality not available')
            }
        },
        computed: {
            updateBlocked() {
                return !canApplyUpdate()
            },
            readyForPwaInstall() {
                if (!this.offlineSetting) return true
                return this.offlineSetting && this.readyForOffline
            },
            blockKeen() {
                return this.getMode('blockKeen')
            },
            simmulateOffline() {
                return this.getMode('simmulateOffline')
            },
            ...mapState(useOfflineStore, ['readyForOffline']),
            ...mapState(useTestModeStore, { getMode: 'get' }),
            ...mapState(usePartnerConfigStore, { offlineSetting: 'offline' }),
        },
        mounted() {
            this.setReadyForOffline(false)
            // the worker drains the queue while the app is closed, so the stored count is stale
            this.recordsInQueue().catch(e => console.log(e))
            if (this.authenticated()) {
                this.register()
            }
        },
        methods: {
            updateOfflineReadiness() {
                this.setReadyForOffline(this.assetsCached && this.offlineSetting)
            },
            setAssetsCached(cached) {
                this.assetsCached = cached
                this.updateOfflineReadiness()
            },
            async display(message) {
                let recordsInQueue = await this.recordsInQueue()
                this.message = message
                this.show = navigator.serviceWorker !== undefined
            },
            async update() {
                if (!canApplyUpdate()) return

                try {
                    if (getWaitingWorker(activeRegistration)) {
                        await activateWaitingWorker(activeRegistration)
                    }
                }
                catch (e) {
                    this.$sentry?.captureException(e)
                    return
                }

                this.show = false
                this.message = null
                location.reload()
            },
            register() {
                /* eslint-disable no-console */
                const display = this.display
                try {
                    console.log('Registering ServiceWorker')
                    register('/service-worker.js', {
                        registrationOptions: { scope: './', type: 'module' },
                        ready: async (registration) => {
                            activeRegistration = registration
                            if (getWaitingWorker(registration)) {
                                display('app.serviceworker.messages.update')
                            }
                            try {
                                // Fetch user specific assets and add to Cache
                                const response = await fetch('/api/v3/precache-assets');
                                const assetsToCache = await response.json();
                                const urlsToCache = [...new Set(assetsToCache.map(asset => asset.url))];

                                const cache = await caches.open('runtime-cache');
                                if (cache) {
                                    await cache.addAll(urlsToCache);
                                    const cachedAssets = await Promise.all(urlsToCache.map(url => cache.match(url)));
                                    const allAssetsCached = cachedAssets.every(Boolean);
                                    console.log(`Cache 'runtime-cache' is ${allAssetsCached ? 'ready' : 'missing required assets'}`);
                                    this.setAssetsCached(allAssetsCached);
                                }
                                else
                                {
                                    console.log("The 'runtime-cache' was not found.");
                                    this.setAssetsCached(false);
                                }
                            } catch (error) {
                                console.error('Error while checking the cache:', error);
                                this.setAssetsCached(false);
                            }
                        },
                        registered() {
                            console.log('Service Worker registered')
                        },
                        cached() {
                            console.log('Content has been cached for offline use.')
                        },
                        updatefound() {
                            console.log('New content is downloading.')
                        },
                        updated(registration) {
                            activeRegistration = registration
                            console.log('New content is available; please refresh.')
                            display('app.serviceworker.messages.update')
                        },
                        offline() {
                            console.log('No internet connection found. App is running in offline mode.')
                        },
                        error: (error) => {
                            this.setAssetsCached(false)
                            console.error('Error during service worker registration:', error)
                        }
                    })
                }
                catch (e) {
                    console.log(e)
                }
            },
            ...mapPiniaActions(useAuthStore, ['authenticated']),
            ...mapPiniaActions(useCollectStore, ['setLastUploadDate']),
            ...mapPiniaActions(useOfflineStore, ['setReadyForOffline']),
        }
    }
</script>
